import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Gift, CheckCircle, Phone, Mail, User, ChevronDown, RefreshCw, Cake, Calendar } from 'lucide-react';
import api from '../utils/apiClient';

// Country codes with phone number length validation
const COUNTRY_CODES = [
  { code: 'FJ', name: 'Fiji', dialCode: '+679', minLength: 7, maxLength: 7 },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64', minLength: 9, maxLength: 10 },
  { code: 'AU', name: 'Australia', dialCode: '+61', minLength: 9, maxLength: 9 },
  { code: 'US', name: 'United States', dialCode: '+1', minLength: 10, maxLength: 10 },
  { code: 'CN', name: 'China', dialCode: '+86', minLength: 11, maxLength: 11 },
  { code: 'DIVIDER', name: '──────────────', dialCode: '', minLength: 0, maxLength: 0 },
  { code: 'AF', name: 'Afghanistan', dialCode: '+93', minLength: 9, maxLength: 9 },
  { code: 'AL', name: 'Albania', dialCode: '+355', minLength: 9, maxLength: 9 },
  { code: 'DZ', name: 'Algeria', dialCode: '+213', minLength: 9, maxLength: 9 },
  { code: 'AD', name: 'Andorra', dialCode: '+376', minLength: 6, maxLength: 9 },
  { code: 'AO', name: 'Angola', dialCode: '+244', minLength: 9, maxLength: 9 },
  { code: 'AR', name: 'Argentina', dialCode: '+54', minLength: 10, maxLength: 11 },
  { code: 'AM', name: 'Armenia', dialCode: '+374', minLength: 8, maxLength: 8 },
  { code: 'AT', name: 'Austria', dialCode: '+43', minLength: 10, maxLength: 13 },
  { code: 'AZ', name: 'Azerbaijan', dialCode: '+994', minLength: 9, maxLength: 9 },
  { code: 'BH', name: 'Bahrain', dialCode: '+973', minLength: 8, maxLength: 8 },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880', minLength: 10, maxLength: 10 },
  { code: 'BY', name: 'Belarus', dialCode: '+375', minLength: 9, maxLength: 9 },
  { code: 'BE', name: 'Belgium', dialCode: '+32', minLength: 9, maxLength: 9 },
  { code: 'BR', name: 'Brazil', dialCode: '+55', minLength: 10, maxLength: 11 },
  { code: 'CA', name: 'Canada', dialCode: '+1', minLength: 10, maxLength: 10 },
  { code: 'CL', name: 'Chile', dialCode: '+56', minLength: 9, maxLength: 9 },
  { code: 'CO', name: 'Colombia', dialCode: '+57', minLength: 10, maxLength: 10 },
  { code: 'DE', name: 'Germany', dialCode: '+49', minLength: 10, maxLength: 11 },
  { code: 'EG', name: 'Egypt', dialCode: '+20', minLength: 10, maxLength: 10 },
  { code: 'FR', name: 'France', dialCode: '+33', minLength: 9, maxLength: 9 },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', minLength: 10, maxLength: 10 },
  { code: 'GR', name: 'Greece', dialCode: '+30', minLength: 10, maxLength: 10 },
  { code: 'IN', name: 'India', dialCode: '+91', minLength: 10, maxLength: 10 },
  { code: 'ID', name: 'Indonesia', dialCode: '+62', minLength: 10, maxLength: 12 },
  { code: 'IE', name: 'Ireland', dialCode: '+353', minLength: 9, maxLength: 9 },
  { code: 'IL', name: 'Israel', dialCode: '+972', minLength: 9, maxLength: 9 },
  { code: 'IT', name: 'Italy', dialCode: '+39', minLength: 10, maxLength: 10 },
  { code: 'JP', name: 'Japan', dialCode: '+81', minLength: 10, maxLength: 10 },
  { code: 'KR', name: 'South Korea', dialCode: '+82', minLength: 9, maxLength: 10 },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', minLength: 9, maxLength: 10 },
  { code: 'MX', name: 'Mexico', dialCode: '+52', minLength: 10, maxLength: 10 },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', minLength: 9, maxLength: 9 },
  { code: 'PH', name: 'Philippines', dialCode: '+63', minLength: 10, maxLength: 10 },
  { code: 'PK', name: 'Pakistan', dialCode: '+92', minLength: 10, maxLength: 10 },
  { code: 'PL', name: 'Poland', dialCode: '+48', minLength: 9, maxLength: 9 },
  { code: 'PT', name: 'Portugal', dialCode: '+351', minLength: 9, maxLength: 9 },
  { code: 'RU', name: 'Russia', dialCode: '+7', minLength: 10, maxLength: 10 },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', minLength: 9, maxLength: 9 },
  { code: 'SG', name: 'Singapore', dialCode: '+65', minLength: 8, maxLength: 8 },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', minLength: 9, maxLength: 9 },
  { code: 'ES', name: 'Spain', dialCode: '+34', minLength: 9, maxLength: 9 },
  { code: 'SE', name: 'Sweden', dialCode: '+46', minLength: 9, maxLength: 9 },
  { code: 'CH', name: 'Switzerland', dialCode: '+41', minLength: 9, maxLength: 9 },
  { code: 'TH', name: 'Thailand', dialCode: '+66', minLength: 9, maxLength: 9 },
  { code: 'TR', name: 'Turkey', dialCode: '+90', minLength: 10, maxLength: 10 },
  { code: 'AE', name: 'UAE', dialCode: '+971', minLength: 9, maxLength: 9 },
  { code: 'VN', name: 'Vietnam', dialCode: '+84', minLength: 9, maxLength: 10 },
];

export default function LoyaltySignupForm({ variant = 'default', className = '' }) {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [couponData, setCouponData] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[0]);
  const [emailCountdown, setEmailCountdown] = useState(0);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendingSms, setResendingSms] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    birthday: ''
  });

  // Countdown timers
  useEffect(() => {
    if (emailCountdown > 0) {
      const timer = setTimeout(() => setEmailCountdown(emailCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [emailCountdown]);

  useEffect(() => {
    if (smsCountdown > 0) {
      const timer = setTimeout(() => setSmsCountdown(smsCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [smsCountdown]);

  useEffect(() => {
    if (success && couponData) {
      if (couponData.email_sent) setEmailCountdown(59);
      if (couponData.sms_sent) setSmsCountdown(59);
    }
  }, [success, couponData]);

  const handleResendEmail = async () => {
    if (!couponData?.signup_id || emailCountdown > 0) return;
    setResendingEmail(true);
    try {
      const res = await api.post(`/loyalty/resend/${couponData.signup_id}?notification_type=email`);
      const data = res.data;
      if (data.email_sent) {
        toast.success('Email resent successfully!');
        setEmailCountdown(59);
      } else if (data.errors?.length) {
        toast.error(data.errors[0]);
      }
    } catch (err) {
      toast.error('Failed to resend email');
    }
    setResendingEmail(false);
  };

  const handleResendSms = async () => {
    if (!couponData?.signup_id || smsCountdown > 0) return;
    setResendingSms(true);
    try {
      const res = await api.post(`/loyalty/resend/${couponData.signup_id}?notification_type=sms`);
      const data = res.data;
      if (data.sms_sent) {
        toast.success('SMS resent successfully!');
        setSmsCountdown(59);
      } else if (data.errors?.length) {
        toast.error(data.errors[0]);
      }
    } catch (err) {
      toast.error('Failed to resend SMS');
    }
    setResendingSms(false);
  };

  const validatePhone = (phoneDigits) => {
    const { minLength, maxLength, name } = selectedCountry;
    const len = phoneDigits.length;
    if (len < minLength) return `Phone number for ${name} requires at least ${minLength} digits`;
    if (len > maxLength) return `Phone number for ${name} should not exceed ${maxLength} digits`;
    return null;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!selectedCountry || selectedCountry.code === 'DIVIDER') newErrors.countryCode = 'Please select a country code';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      const phoneError = validatePhone(phoneDigits);
      if (phoneError) newErrors.phone = phoneError;
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!formData.email.includes('@') || !formData.email.includes('.')) {
      newErrors.email = 'Please enter a valid email address';
    }
    // Birthday is optional, no validation needed
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    console.log('[LoyaltySignup] Form submitted', { formData, selectedCountry });
    
    if (!validateForm()) {
      console.log('[LoyaltySignup] Validation failed', errors);
      toast.error('Please fill in all required fields correctly');
      return;
    }
    setSubmitting(true);

    const phoneDigits = formData.phone.replace(/\D/g, '');
    const fullPhone = `${selectedCountry.dialCode}${phoneDigits}`;
    
    const payload = {
      name: formData.name,
      email: formData.email,
      phone: fullPhone,
      country_code: selectedCountry.dialCode,
      country_name: selectedCountry.name,
      phone_local: phoneDigits,
      birthday: formData.birthday || null
    };
    
    console.log('[LoyaltySignup] Sending request:', payload);

    try {
      const res = await api.post('/loyalty/signup', payload);
      console.log('[LoyaltySignup] Response:', res.data);

      const data = res.data;

      if (data.success) {
        setCouponData(data);
        setSuccess(true);
        toast.success(data.is_existing ? 'Welcome back!' : 'Welcome to the Loyalty Program!');
      } else {
        console.error('[LoyaltySignup] API returned success=false:', data);
        const errorMsg = data.message || 'Something went wrong. Please try again.';
        setSubmitError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error('[LoyaltySignup] Error:', err);
      console.error('[LoyaltySignup] Error response:', err.response?.data);
      console.error('[LoyaltySignup] Error status:', err.response?.status);
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to register. Please check your connection and try again.';
      setSubmitError(errorMessage);
      toast.error(errorMessage);
    }
    setSubmitting(false);
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    
    // Remove all non-digit characters first for processing
    let digits = value.replace(/\D/g, '');
    
    // If user accidentally included the country dial code, strip it
    const dialCodeDigits = selectedCountry.dialCode.replace(/\D/g, '');
    if (digits.startsWith(dialCodeDigits) && digits.length > selectedCountry.maxLength) {
      digits = digits.substring(dialCodeDigits.length);
    }
    
    // For US/Canada, also strip leading 1 if they entered it
    if ((selectedCountry.code === 'US' || selectedCountry.code === 'CA') && digits.startsWith('1') && digits.length > 10) {
      digits = digits.substring(1);
    }
    
    // Allow only digits, limit to max length
    const limitedDigits = digits.substring(0, selectedCountry.maxLength);
    
    // Format the display (just digits, no formatting for simplicity)
    setFormData(prev => ({ ...prev, phone: limitedDigits }));
    if (errors.phone) setErrors(prev => ({ ...prev, phone: '' }));
  };
  
  // Generate placeholder based on country
  const getPhonePlaceholder = () => {
    const { code, minLength } = selectedCountry;
    
    // Common placeholders for popular countries
    const placeholders = {
      'US': '2125551234',
      'CA': '4165551234', 
      'FJ': '7001234',
      'AU': '412345678',
      'NZ': '211234567',
      'GB': '7911123456',
      'IN': '9876543210'
    };
    
    if (placeholders[code]) return placeholders[code];
    
    // Generic placeholder based on length
    return '9'.repeat(minLength);
  };

  const isHomepage = variant === 'homepage';

  // Success state
  if (success && couponData) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`${isHomepage ? 'bg-white/10 backdrop-blur-md' : 'bg-white dark:bg-gray-800'} rounded-3xl shadow-2xl max-w-md w-full p-8 text-center mx-auto ${className}`}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring' }}
          className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-6"
        >
          <CheckCircle className="w-10 h-10 text-white" />
        </motion.div>

        <h2 className={`font-heading text-3xl ${isHomepage ? 'text-white' : 'text-gray-900 dark:text-white'} mb-2`}>
          Vinaka
        </h2>
        <h3 className={`font-heading text-2xl ${isHomepage ? 'text-white/90' : 'text-gray-700 dark:text-gray-200'} mb-6`}>
          Welcome to the Family!
        </h3>

        <p className={`${isHomepage ? 'text-white/80' : 'text-gray-600 dark:text-gray-300'} mb-6`}>
          You&apos;re now a member of the Bulaccino Cafe Rewards Family! Earn points on every purchase and unlock exclusive perks.
        </p>

        <div className={`flex items-center justify-center gap-2 text-sm ${isHomepage ? 'text-primary-foreground' : 'text-primary'} mb-4`}>
          <Gift className="w-4 h-4" />
          <span>Your membership is now active!</span>
        </div>

        {/* Resend Buttons */}
        <div className={`${isHomepage ? 'bg-white/10' : 'bg-gray-50 dark:bg-gray-700'} rounded-xl p-4`}>
          <p className={`text-sm ${isHomepage ? 'text-white/70' : 'text-gray-600 dark:text-gray-300'} mb-3`}>Didn&apos;t receive your confirmation?</p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={handleResendEmail}
              disabled={emailCountdown > 0 || resendingEmail}
              variant="outline"
              size="sm"
              className="text-xs"
              data-testid="loyalty-resend-email-btn"
            >
              {resendingEmail ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Mail className="w-3 h-3 mr-1" />}
              {emailCountdown > 0 ? `Resend Email (${emailCountdown}s)` : 'Resend Email'}
            </Button>
            <Button
              onClick={handleResendSms}
              disabled={smsCountdown > 0 || resendingSms}
              variant="outline"
              size="sm"
              className="text-xs"
              data-testid="loyalty-resend-sms-btn"
            >
              {resendingSms ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Phone className="w-3 h-3 mr-1" />}
              {smsCountdown > 0 ? `Resend SMS (${smsCountdown}s)` : 'Resend SMS'}
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  // Form
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${isHomepage ? 'bg-white/10 backdrop-blur-md border border-white/20' : 'bg-white dark:bg-card'} rounded-3xl p-6 md:p-8 max-w-md w-full mx-auto shadow-xl ${className}`}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name */}
        <div className="space-y-2">
          <Label className={`flex items-center gap-2 ${isHomepage ? 'text-white' : 'text-foreground'}`}>
            <User className="w-4 h-4 text-primary" />
            Name <span className="text-red-500">*</span>
          </Label>
          <Input
            type="text"
            value={formData.name}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, name: e.target.value }));
              if (errors.name) setErrors(prev => ({ ...prev, name: '' }));
            }}
            placeholder="Enter your name"
            className={`${isHomepage ? 'bg-white/10 border-white/30 text-white placeholder:text-white/50' : ''} ${errors.name ? 'border-red-500' : ''}`}
            data-testid="loyalty-name-input"
          />
          {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label className={`flex items-center gap-2 ${isHomepage ? 'text-white' : 'text-foreground'}`}>
            <Phone className="w-4 h-4 text-primary" />
            Phone Number <span className="text-red-500">*</span>
          </Label>
          <div className="relative">
            <select
              value={selectedCountry.code}
              onChange={(e) => {
                const country = COUNTRY_CODES.find(c => c.code === e.target.value);
                if (country && country.code !== 'DIVIDER') {
                  setSelectedCountry(country);
                }
              }}
              className={`w-full px-4 py-2.5 rounded-lg border appearance-none cursor-pointer
                ${isHomepage 
                  ? 'bg-white/10 border-white/30 text-white' 
                  : 'bg-background border-input text-foreground'
                } ${errors.countryCode ? 'border-red-500' : ''}`}
              data-testid="loyalty-country-select"
            >
              {COUNTRY_CODES.map((country) => (
                <option 
                  key={country.code} 
                  value={country.code}
                  disabled={country.code === 'DIVIDER'}
                  className="bg-background text-foreground"
                >
                  {country.code === 'DIVIDER' ? country.name : `${country.dialCode} ${country.name}`}
                </option>
              ))}
            </select>
            <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${isHomepage ? 'text-white/70' : 'text-muted-foreground'}`} />
          </div>
          <Input
            type="tel"
            value={formData.phone}
            onChange={handlePhoneChange}
            placeholder={getPhonePlaceholder()}
            maxLength={selectedCountry.maxLength + 2}
            className={`${isHomepage ? 'bg-white/10 border-white/30 text-white placeholder:text-white/50' : ''} ${errors.phone ? 'border-red-500' : ''} font-mono tracking-wide`}
            data-testid="loyalty-phone-input"
          />
          <p className={`text-xs ${isHomepage ? 'text-white/60' : 'text-muted-foreground'}`}>
            Enter {selectedCountry.minLength === selectedCountry.maxLength 
              ? selectedCountry.minLength 
              : `${selectedCountry.minLength}-${selectedCountry.maxLength}`} digits without country code ({selectedCountry.dialCode} will be added automatically)
          </p>
          {errors.phone && <p className="text-red-500 text-xs">{errors.phone}</p>}
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label className={`flex items-center gap-2 ${isHomepage ? 'text-white' : 'text-foreground'}`}>
            <Mail className="w-4 h-4 text-primary" />
            Email Address <span className="text-red-500">*</span>
          </Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, email: e.target.value }));
              if (errors.email) setErrors(prev => ({ ...prev, email: '' }));
            }}
            placeholder="your@email.com"
            className={`${isHomepage ? 'bg-white/10 border-white/30 text-white placeholder:text-white/50' : ''} ${errors.email ? 'border-red-500' : ''}`}
            data-testid="loyalty-email-input"
          />
          {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
        </div>

        {/* Birthday - Optional */}
        <div className="space-y-2">
          <Label className={`flex items-center gap-2 ${isHomepage ? 'text-white' : 'text-foreground'}`}>
            <Cake className="w-4 h-4 text-primary" />
            Birthday <span className={`text-xs ${isHomepage ? 'text-white/60' : 'text-muted-foreground'}`}>(optional)</span>
          </Label>
          <p className={`text-xs ${isHomepage ? 'text-white/70' : 'text-muted-foreground'} mb-1`}>
            Enter your birthday to receive a free cup of coffee on your birthday!
          </p>
          <Input
            type="date"
            value={formData.birthday}
            onChange={(e) => setFormData(prev => ({ ...prev, birthday: e.target.value }))}
            className={`${isHomepage ? 'bg-white/10 border-white/30 text-white [color-scheme:dark]' : ''}`}
            data-testid="loyalty-birthday-input"
          />
        </div>

        {/* Error Message */}
        {submitError && (
          <div className="p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg">
            <p className="text-red-700 dark:text-red-300 text-sm font-medium">
              ⚠️ {submitError}
            </p>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={submitting}
          className="w-full rounded-full bg-primary hover:bg-primary/90 text-white py-6 text-base mt-4"
          data-testid="loyalty-submit-btn"
        >
          {submitting ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              Joining...
            </>
          ) : (
            <>
              <Gift className="w-4 h-4 mr-2" />
              Join Loyalty Program
            </>
          )}
        </Button>
      </form>
    </motion.div>
  );
}
